import { createSlice } from '@reduxjs/toolkit';
import { getFromToFilterFromTimespan } from 'components/util/datePicker/FromToDatePicker';
import { v4 as uuid } from 'uuid';
import { TimeSpan } from '../../@types/dashboard';
import { dispatch } from '../store';
import { translator } from 'translation/useTranslator';
import { AiFeedbackState } from '../../@types/aiFeedback';
import { getDateFromObject } from '@nps.today/component-library';
import { rejectWithError } from '../dispatchError';

const { from: initialFrom, to: initialTo } = getFromToFilterFromTimespan(TimeSpan.LAST_30_DAYS);
export const initialState: AiFeedbackState = {
  timespan: TimeSpan.LAST_30_DAYS,
  fromDateFilter: initialFrom,
  toDateFilter: initialTo,
  aiFeedbackCampaignKeys: ['activeCampaigns'],
  conversationId: uuid(),
  messages: [
    {
      content:
        translator?.feedbackAiGreetingMessage() ||
        "Hello! I'm your AI assistant for feedback analysis. I can help you understand trends, sentiments, and key themes in your survey responses. Ask me anything about your feedback data!",
      id: uuid(),
      type: 'ai',
      streamComplete: true,
      feedbackReceived: true,
      isGreeting: true
    }
  ]
};

const slice = createSlice({
  name: 'aiFeedback',
  initialState,
  reducers: {
    setDateFilter(state, action) {
      state.fromDateFilter = action.payload.from;
      state.toDateFilter = action.payload.to;
      state.timespan = action.payload.timespan;
    },
    setCampaignKeys(state, action) {
      state.aiFeedbackCampaignKeys = action.payload;
    },
    setMessages(state, action) {
      if (typeof action.payload === 'function') {
        const prev = state.messages;
        const next = action.payload(prev);
        state.messages = next;
      } else {
        state.messages = action.payload;
      }
    },
    resetChat(state) {
      state.conversationId = uuid();
      state.messages = [
        {
          content:
            translator?.feedbackAiGreetingMessage() ||
            "Hello! I'm your AI assistant for feedback analysis. I can help you understand trends, sentiments, and key themes in your survey responses. Ask me anything about your feedback data!",
          id: uuid(),
          type: 'ai',
          streamComplete: true,
          feedbackReceived: true,
          isGreeting: true
        }
      ];
    },
    markMessageAsFeedbackReceived(state, action) {
      const prev = [...state.messages];
      const index = prev.findIndex((msg) => msg.id === action.payload.id);
      if (index !== -1) {
        prev[index] = {
          ...prev[index],
          feedbackReceived: true
        };
      }
      state.messages = prev;
    }
  }
});

export default slice.reducer;

export const updateAiFeedbackDateFilter = ({
  from,
  to,
  timespan
}: {
  from: any;
  to: any;
  timespan: TimeSpan;
}) => {
  return () => {
    dispatch(
      slice.actions.setDateFilter({
        from: getDateFromObject(from),
        to: getDateFromObject(to),
        timespan: timespan
      })
    );

    return Promise.resolve({
      defaultSuccessMessage: translator?.filterUpdated() ?? 'AI feedback date filter updated'
    });
  };
};

export const updateAiFeedbackCampaignKeys = (campaignDropdownKeys: (number | string)[]) => {
  return () => {
    dispatch(slice.actions.setCampaignKeys(campaignDropdownKeys));
    return Promise.resolve({
      defaultSuccessMessage:
        translator?.selectedCampaignUpdated() ?? 'Selected AI feedback campaign updated'
    });
  };
};

type MarkMessageAsFeedbackReceivedProps = {
  messageId: string;
};

export const markMessageAsFeedbackReceived = ({
  messageId
}: MarkMessageAsFeedbackReceivedProps) => {
  return async () => {
    try {
      dispatch(slice.actions.markMessageAsFeedbackReceived({ id: messageId }));
      return await Promise.resolve({
        result: messageId
      });
    } catch (error) {
      return await rejectWithError({
        error: error
      });
    }
  };
};

export const setMessages = (messages: {
  messages:
    | AiFeedbackState['messages']
    | ((prev: AiFeedbackState['messages']) => AiFeedbackState['messages']);
}) => {
  return async () => {
    try {
      dispatch(slice.actions.setMessages(messages));
      return await Promise.resolve({
        result: messages
      });
    } catch (error) {
      return await rejectWithError({
        error
      });
    }
  };
};

export const resetChat = () => {
  return async () => {
    try {
      dispatch(slice.actions.resetChat());
      return await Promise.resolve({
        result: true
      });
    } catch (error) {
      return await rejectWithError({
        error
      });
    }
  };
};
